<template>
	<div ref="verify-pin-dialog" tabindex="0" class="verify-parent-pin-code dialog-mask">
		<div class="modal-dialog">
			<div class="modal-content">
				<div class="modal-header">
					<h4 v-if="!isNewCodeStep" class="modal-title">
						{{ $t(`checkout.parental-approval.${step}.title`) }}

						<div v-if="isConfirmStep" class="tooltip-payment-child-account">
							<v-popover placement="right" popover-base-class="v-tooltip v-popover" trigger="click">
								<template slot="popover">
									<div v-close-popover class="tooltip-close" />
								</template>
								<i class="tooltip-button" @click="handleTooltipClick"></i>
								<template slot="popover" class="tooltip-template">
									{{ $t('checkout.parental-approval.confirm.tooltip') }}
								</template>
							</v-popover>
						</div>
					</h4>
				</div>
				<div class="modal-body">
					<p>
						{{ $t(`checkout.parental-approval.${step}.description`) }}
					</p>

					<pinCodeInput
						v-if="isVerifyStep"
						v-model="inputCode"
						class="verify-parent-code-input"
						autofocus
						:has-error="$v.inputCode.$error || isInvalidCode"
						:error-text="inputErrorText"
						:length="codeLength"
						:disabled="actionButtonLoading || newCodeLoading"
						@fulfill="action"
					/>
				</div>
				<div class="modal-footer">
					<div class="btn-container btn-container--cancel">
						<FormButton
							:label="$t(`${isConfirmStep ? 'cancel' : 'close'}`)"
							:button-click="closeModal"
							:disabled="actionButtonLoading || newCodeLoading"
						/>
					</div>

					<div class="btn-container btn-container--confirm">
						<FormButton
							class="btn-black"
							:label="$t(actionButtonName)"
							:button-click="action"
							:loading="actionButtonLoading"
							:disabled="newCodeLoading"
						/>
					</div>

					<div v-if="isVerifyStep" class="btn-container btn-container--new-code">
						<button
							v-if="!newCodeLoading"
							class="btn-newcode"
							:disabled="actionButtonLoading"
							@click="requestNewCode"
						>
							{{ $t('checkout.parental-approval.verify.send-new-code') }}
						</button>

						<div v-else class="spinner-circle-container">
							<span class="spinner-circle"></span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PinCodeInput from '@/components/common/pin-code-input';
import { FormButton } from '@arena/toolkit';
import { required, minLength } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';

const CONFIRM = 'confirm';
const VERIFY = 'verify';
const NEW_CODE = 'new-code';
const EXPIRED = 'expired';
const INVALID_CODE = '002-400-0013';
const EXPIRED_CODE = '002-400-0014';

export default {
	name: 'ModalParentPinVerification',
	components: {
		PinCodeInput,
		FormButton,
	},
	data() {
		return {
			step: CONFIRM, // confirm, verify, new-code or expired
			inputCode: '',
			codeLength: 6,
			actionButtonLoading: false,
			newCodeLoading: false,
			errorCode: '',
		};
	},
	validations() {
		return {
			inputCode: {
				required,
				minLength: minLength(this.codeLength),
			},
		};
	},
	computed: {
		...mapGetters({
			isFraudErrorCode: 'isFraudErrorCode',
		}),
		isIngamePurchase() {
			return this.$route.name === 'in-game-purchase' || this.$store.state.appType === 'game';
		},
		isConfirmStep() {
			return this.step === CONFIRM;
		},
		isVerifyStep() {
			return this.step === VERIFY;
		},
		isNewCodeStep() {
			return this.step === NEW_CODE;
		},
		isInvalidCode() {
			return this.errorCode === INVALID_CODE;
		},
		isExpiredCode() {
			return this.errorCode === EXPIRED_CODE;
		},
		inputErrorText() {
			if (!this.$v.inputCode.required || !this.$v.inputCode.minLength) {
				return 'checkout.parental-approval.please-enter-verification-code';
			}
			if (this.isInvalidCode) {
				return 'checkout.parental-approval.invalid-code';
			}
			return '';
		},
		actionButtonName() {
			switch (this.step) {
				case EXPIRED:
					return 'checkout.parental-approval.expired.action';
				case VERIFY:
					return 'verify';
				default:
					return 'confirm';
			}
		},
	},
	watch: {
		inputCode(code) {
			this.$nextTick(() => {
				this.$v.inputCode.$reset();
				if (this.isInvalidCode && code.length < this.codeLength) this.errorCode = '';
			});
		},
	},
	mounted() {
		this.$refs['verify-pin-dialog'].focus(); // move the focus to the modal in order to remove it from the PG button and prevent opening multiple times the pin verification modal
		this.$store.commit('setPgLoading', false);
		document.addEventListener('keyup', this.handleEscKey);
		this.$a2s.view('parental_imp');
		dataLayer.push({
			event: 'Payment Restriction Verification Required',
		});
	},
	destroyed() {
		document.removeEventListener('keyup', this.handleEscKey);
	},
	methods: {
		handleTooltipClick() {
			this.$a2s.click('parental_tooltip');
		},
		closeModal() {
			this.$a2s.click('parental_cancel');
			dataLayer.push({
				event: 'Payment Restriction Verification Result',
				parentalControl_Result: 'Cancel',
			});
			if (this.region === 'tw') {
				this.$store.commit('setCheckoutIdSession', ''); // we should clear the checkout session id for TW in game case
				this.$router.go(-1); // since tw has only 1 payment method there is no payment methods options, if user click cancel we should redirect to the previous page
			}
			this.$close(false);
		},
		handleEscKey(e) {
			if (e.code === 'Escape') {
				this.closeModal();
			}
		},
		async requestCode() {
			return await this.$checkoutAPI.generateOtpCode();
		},
		async requestNewCode() {
			try {
				this.newCodeLoading = true;
				const response = await this.requestCode();
				this.step = NEW_CODE;
				this.newCodeLoading = false;

				return response;
			} catch (error) {
				this.errorHandler(error);
			}
		},
		async action() {
			try {
				switch (this.step) {
					case CONFIRM: {
						this.$a2s.click('parental_confirm');
						this.actionButtonLoading = true;
						await this.requestCode();
						this.step = VERIFY;
						this.actionButtonLoading = false;
						break;
					}
					case VERIFY: {
						this.$v.inputCode.$touch();
						this.$a2s.click('parental_verify');
						if (!this.$v.inputCode.$invalid) {
							this.actionButtonLoading = true;
							await this.$store.dispatch('beginPayment', this.inputCode);
							this.$close(true);
							dataLayer.push({
								event: 'Payment Restriction Verification Result',
								parentalControl_Result: 'Success',
							});
						}
						break;
					}
					case NEW_CODE: {
						this.inputCode = '';
						this.$v.inputCode.$reset();
						this.step = VERIFY;
						break;
					}
					case EXPIRED: {
						this.actionButtonLoading = true;
						await this.requestCode();
						this.actionButtonLoading = false;
						this.step = NEW_CODE;

						break;
					}
					// no-default
				}
			} catch (error) {
				this.errorHandler(error);
			}
		},
		errorHandler(error) {
			this.actionButtonLoading = false;
			this.newCodeLoading = false;

			this.errorCode =
				error.response && error.response.data && error.response.data.code
					? error.response.data.code
					: error.response.status || '';

			const errorMessage = this.$te(`error.form.${this.errorCode}`)
				? `${this.$t(`error.form.${this.errorCode}`, 'en')} ${this.errorCode}`
				: this.errorCode;

			if (this.isInvalidCode) {
				// if is invalid error code then it will be displayed the error message under the pin code verification
				this.failureDataTracking(this.$t('checkout.parental-approval.invalid-code', 'en'));
				return;
			} else if (this.isExpiredCode) {
				this.step = EXPIRED;
				this.failureDataTracking(this.$t('checkout.parental-approval.expired.title', 'en'));
				return;
			} else if (this.isFraudErrorCode) {
				// if it's fraud error code (check isFraudErrorCode store getter for the fraud error codes) then the modal will be closed and the fraud message UI be displayed
				this.$close(false);
				this.failureDataTracking(errorMessage);
			} else {
				this.failureDataTracking(errorMessage);

				this.$router.replace({
					name: this.isIngamePurchase ? 'in-game-purchase-failed' : 'purchase-failed',
					query: {
						...(this.errorCode && { error_code: this.errorCode }),
					},
				});
				this.$error(error);
			}
		},
		failureDataTracking(message) {
			dataLayer.push({
				event: 'Payment Restriction Verification Result',
				parentalControl_Result: 'Failure',
				error_message: message,
			});
		},
	},
};
</script>

<style scoped lang="less">
.verify-parent-pin-code {
	.tooltip-payment-child-account {
		position: relative;
		display: inline-block;
		top: -3px;
		margin-left: 5px;
		font-size: 13px;
		color: #666666;
		> .tooltip-button {
			margin-left: 5px;
		}

		.v-popover {
			@bg: #7b7f8c;
			.tooltip-button {
				display: inline-flex;
				justify-content: center;
				align-items: center;
				border-radius: 50%;
				font-family: Arial;
				font-style: normal;
				font-weight: 700;
				font-size: 8.5px;
				width: 14px;
				height: 14px;
				background-color: #4f5159;
				color: hsla(0, 0%, 100%, 0.8);
				&:before {
					content: '?';
					line-height: 1;
					margin-left: -1px;
				}
				&:hover {
					background-color: @bg;
					cursor: pointer;
				}
			}

			&.open {
				.tooltip-button {
					background-color: @bg;
				}
			}
		}
	}

	.modal-body {
		.verify-parent-code-input {
			max-width: 360px;
			margin-left: auto;
			margin-right: auto;
			margin-top: 35px;
		}

		:deep(.help-block) {
			margin-bottom: -26px;
		}
		.form-group {
			margin-bottom: 0;
		}
	}

	.modal-footer {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		margin-left: -5px;
		margin-right: -5px;
		.btn-container {
			flex: 0 0 50%;
			max-width: 50%;
			padding: 0 5px;

			&--new-code {
				display: flex;
				justify-content: center;
				align-items: center;
				flex: 0 0 100%;
				max-width: 100%;
				text-align: center;
				margin-top: 16px;
				height: 34px;
				.spinner-circle {
					width: 24px;
					height: 24px;
					margin: 0;
				}
			}

			@media (max-width: 479px) {
				flex: 0 0 100%;
				max-width: 100%;
				padding: 0;

				&--confirm {
					order: -1;
				}
			}
		}

		.btn-newcode {
			color: #262626;
			background: transparent;
			border: none;
			padding: 10px;
			text-decoration: underline;
			text-underline-position: under;
			font-weight: bold;
			text-transform: initial;
			font-size: 12px;
			&:hover {
				opacity: 0.7;
			}
			&:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}
	}
}
</style>
